import { BaseNodeModel } from '@app/models';
import {
  Design,
  DesignModule,
  DesignModuleNode,
  DesignModuleNodeConfigReferenceType,
  DesignObject,
  NodeTypeName,
  ObjectClass,
  ValueOrVariable,
  ValueOrVariableType
} from '@app/types';
import { safeParseInt } from '../string';
import { normalizeDisambiguationPrompt } from './normalize-disambiguation-prompt';
import { normalizeNoInputTimeoutOverride } from './normalize-no-input-timeout-override';
import { normalizePrompt } from './normalize-prompt';

type Normalizable = DesignObject | DesignModuleNode | BaseNodeModel;

export type Normalizer = <
  T extends Normalizable
>(
  item: T,
  serialized: Design,
  /** If @see item is a node, then this is the module it is in. */
  appModule?: DesignModule
) => T;

/** Normalize a @see DesignObject or @see DesignModuleNode. */
export const normalizeObjectOrNode = <T extends Normalizable>(
  item: T,
  serialized: Design = window.apm.serialized,
  appModule?: DesignModule
): T => {
  item = normalizeOasysRename(item, serialized);
  item = normalizeInputs(item, serialized);

  // Some old @see DesignObject items have localVariables they should not.
  if ('localVariables' in item) {
    delete item.localVariables;
  }

  switch ('_class' in item ? item._class : item.constructor.name as ObjectClass) {
    case ObjectClass.flushPrompts:
      return normalizeFlushPrompts(item, serialized);
    case ObjectClass.input:
      item = normalizeInput(item, serialized);
      return normalizeInputOrRichText(item, serialized);
    case ObjectClass.prompt:
      return normalizePrompt(item, serialized, appModule);
    case ObjectClass.promptReference:
      return normalizePromptReference(item, serialized);
    case ObjectClass.richText:
      return normalizeInputOrRichText(item, serialized);
    case ObjectClass.sessionCompletion:
      return normalizeSessionCompletion(item, serialized);
  }

  return item;
};

const normalizeInputs: Normalizer = (item) => {
  const { config } = item.props;
  if (config.inputs != null && !(config.inputs instanceof Array)) {
    config.inputs = [];
  }

  return item;
};

/** Migrate Amelia → OASYS rename on load. */
const normalizeOasysRename: Normalizer = (item) => {
  if ('_class' in item && (item._class as string) === 'AmeliaNodeModel') {
    item._class = ObjectClass.oasys;
  }

  if ((item.props.nodeTypeName as string) === 'Amelia') {
    item.props.nodeTypeName = NodeTypeName.oasys;
  }

  return item;
};

const normalizeSessionCompletion: Normalizer = (item) => {
  if ((item.props.nodeTypeName as string) === 'Session Completion') {
    item.props.nodeTypeName = NodeTypeName.sessionCompletion;
  }

  return item;
};

const normalizeFlushPrompts: Normalizer = (item) => {
  if ((item.props.nodeTypeName as string) === 'FLUSH PROMPTS') {
    item.props.nodeTypeName = NodeTypeName.flushPrompts;
  }

  return item;
};

const normalizeInput: Normalizer = (item, serialized) => {
  const { inputNumberVariables } = serialized.rootSettings.features;
  const { config } = item.props;
  const convertNumericProp = (!inputNumberVariables) ? (
    // We need to convert back from ValueOrVariable if we upgraded the input prior to adding DesignFeatureFlag.inputNumberVariables.
    convertValueOrVariableToNumber
  ) : convertNumberToValueOrVariable;

  config.dtmfInterDigitTimeout = convertNumericProp(config.dtmfInterDigitTimeout);
  config.lengthOfSilence = convertNumericProp(config.lengthOfSilence);
  config.maxIntentTimeout = convertNumericProp(config.maxIntentTimeout);
  return item;
};

const normalizeInputOrRichText: Normalizer = (item, serialized) => {
  const { config } = item.props;
  const { noInputTimeout } = config;

  normalizeDisambiguationPrompt(config, serialized);
  normalizeNoInputTimeoutOverride(config, noInputTimeout);

  return item;
};

const variableSearch = /^\s*((app|mod)\.\S+)\s*$/;
const normalizePromptReference: Normalizer = (item) => {
  const { config } = item.props;
  if (!config.referenceType) {
    // Handle cases where reference type is unset.
    if (
      !config.variableReference
      && variableSearch.test(config.promptReference!)
    ) {
      // Handle cases where variables were in `promptReference` instead of `variableReference`.
      config.variableReference = config.promptReference;
      delete config.promptReference;
    }
    config.referenceType ??= (config.variableReference) ? (
      DesignModuleNodeConfigReferenceType.variable
    ) : DesignModuleNodeConfigReferenceType.prompt;
  }

  if (
    config.referenceType !== DesignModuleNodeConfigReferenceType.prompt
    && config.promptReference
  ) {
    delete config.promptReference;
  }

  if (
    config.referenceType !== DesignModuleNodeConfigReferenceType.variable
    && config.variableReference
  ) {
    delete config.variableReference;
  }

  return item;
};

const convertValueOrVariableToNumber = (
  value: ValueOrVariable<number | null> | number | string | undefined
): number | '' => {
  if (typeof value === 'number' || value === '') {
    return value;
  } else if (!value) {
    return '';
  } else if (typeof value === 'string') {
    return parseInt(value);
  }

  return (value.type !== ValueOrVariableType.value) ? '' : (
    value.value ?? ''
  );
};

const convertNumberToValueOrVariable = (
  value: ValueOrVariable<number | null> | number | string | undefined
): ValueOrVariable<number | null> => {
  if (typeof value === 'object') {
    return value;
  }

  return {
    value: safeParseInt(value),
    type: ValueOrVariableType.value
  };
};
